import { Add } from '@mui/icons-material'
import {
  Alert,
  AlertProps,
  Box,
  Button,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams, GridRowModel } from '@mui/x-data-grid'
import { doc, setDoc, updateDoc } from 'firebase/firestore'
import { useMemo, useState } from 'react'

import {
  useWorkoutSettings,
  WorkoutLocation,
} from '../../../providers/workout-settings'
import { firestore } from '../../firebaseInit'
import TableToolbar from './table-toolbar'

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function locationDoc(id: string) {
  return doc(firestore, 'locations', id)
}

export default function LocationsTable() {
  const { locations, loading, error } = useWorkoutSettings()
  const [name, setName] = useState('')
  const [snackbar, setSnackbar] = useState<Pick<
    AlertProps,
    'children' | 'severity'
  > | null>(null)

  const rows = useMemo(
    () =>
      locations.map((location) => ({
        ...location,
        active: location.active ?? true,
      })),
    [locations]
  )

  const handleCreate = async () => {
    const trimmedName = name.trim()
    const id = slugify(trimmedName)

    if (!trimmedName || !id) return

    try {
      await setDoc(locationDoc(id), {
        name: trimmedName,
        active: true,
        sortOrder: locations.length * 10 + 10,
      })
      setName('')
      setSnackbar({ children: 'Platsen lades till', severity: 'success' })
    } catch (error) {
      console.log(error)
      setSnackbar({ children: 'Platsen kunde inte sparas', severity: 'error' })
    }
  }

  const processRowUpdate = async (newRow: GridRowModel) => {
    const location = newRow as WorkoutLocation

    await setDoc(
      locationDoc(location.id),
      {
        name: location.name,
        active: location.active ?? true,
        sortOrder: Number(location.sortOrder ?? 0),
      },
      { merge: true }
    )

    setSnackbar({ children: 'Platsen uppdaterades', severity: 'success' })
    return newRow
  }

  const handleActiveChange = async (id: string, active: boolean) => {
    try {
      await updateDoc(locationDoc(id), { active })
      setSnackbar({
        children: active ? 'Platsen aktiverades' : 'Platsen inaktiverades',
        severity: 'success',
      })
    } catch (error) {
      console.log(error)
      setSnackbar({ children: 'Platsen kunde inte uppdateras', severity: 'error' })
    }
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', minWidth: 160 },
    { field: 'name', headerName: 'Namn', editable: true, minWidth: 240, flex: 1 },
    {
      field: 'active',
      headerName: 'Aktiv',
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value ? 'Inaktivera plats' : 'Aktivera plats'}>
          <Switch
            size="small"
            checked={Boolean(params.value)}
            onChange={(event) =>
              handleActiveChange(params.row.id, event.target.checked)
            }
          />
        </Tooltip>
      ),
    },
    {
      field: 'sortOrder',
      headerName: 'Sortering',
      type: 'number',
      editable: true,
      minWidth: 120,
    },
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ py: 2 }}>
        <TextField
          label="Ny plats"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyUp={(event) => event.key === 'Enter' && handleCreate()}
          fullWidth
        />
        <Button
          variant="contained"
          startIcon={<Add />}
          disabled={!name.trim()}
          onClick={handleCreate}
          sx={{ minWidth: 140 }}
        >
          Lägg till
        </Button>
      </Stack>
      {!error && (
        <DataGrid
          autoHeight
          experimentalFeatures={{ newEditingApi: true }}
          components={{ Toolbar: TableToolbar }}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          processRowUpdate={processRowUpdate}
          pageSize={10}
          rowsPerPageOptions={[10]}
          hideFooterSelectedRowCount
          disableSelectionOnClick
        />
      )}
      {error && <DataGrid error={error} columns={[]} rows={[]} />}
      {!!snackbar && (
        <Snackbar
          open
          autoHideDuration={5000}
          onClose={() => setSnackbar(null)}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  )
}
